import { flow } from 'fp-ts/function';
import { concat, groupBy, head, isEmpty, isMatch, map, mapValues, mergeWith, pick, reduce, T, values } from 'lodash/fp';

import { ifElse } from '@mews/fp';
import {
    DecimalService,
    divideNumbersAsDecimals,
    multiplyNumbersAsDecimals,
    subtractNumbersAsDecimals,
    sumNumbersAsDecimals,
} from '@mews/framework';

import { Amount, MultiCurrencyAmount, SimpleAmount, TaxValue, UntaxedAmount } from '../types';

// currencyCode is empty string, which can crash the app.
// We have to create ZERO_AMOUNT indirectly, with createZeroAmount.
export const ZERO_AMOUNT: Amount = {
    currency: '',
    grossValue: 0,
    netValue: 0,
    taxValues: [],
};

export const createZeroAmount = (currency: string) => ({ ...ZERO_AMOUNT, currency });
export const createZeroSimpleAmount = (currencyCode: string) => ({ value: 0, currencyCode });

const sumTaxValues = (prevTaxValue: TaxValue, nextTaxValue: TaxValue): TaxValue => ({
    ...prevTaxValue,
    value: sumNumbersAsDecimals(prevTaxValue.value, nextTaxValue.value),
});

export const sumMultipleTaxValues = flow<[TaxValue[], TaxValue[]], TaxValue[], Record<string, TaxValue[]>, TaxValue[]>(
    concat,
    groupBy<TaxValue>('taxRateCode'),
    map<Record<string, TaxValue[]>, TaxValue>((taxValues: TaxValue[]) => taxValues.reduce(sumTaxValues))
);

export const sumAmounts = (prev: Amount, next: Amount): Amount => {
    const {
        netValue: prevNetValue,
        grossValue: prevGrossValue,
        taxValues: prevTaxValues,
        currency: prevCurrency,
    } = prev;
    const {
        netValue: nextNetValue,
        grossValue: nextGrossValue,
        taxValues: nextTaxValues,
        currency: nextCurrency,
    } = next;

    return {
        netValue: sumNumbersAsDecimals(prevNetValue, nextNetValue),
        grossValue: sumNumbersAsDecimals(prevGrossValue, nextGrossValue),
        taxValues: sumMultipleTaxValues(prevTaxValues, nextTaxValues),
        currency: prevCurrency || nextCurrency,
    };
};

export const sumSimpleAmounts = (prev: SimpleAmount, next: SimpleAmount): SimpleAmount => {
    const { value: prevValue, currencyCode: prevCurrency } = prev;
    const { value: nextValue, currencyCode: nextCurrency } = next;

    return {
        value: sumNumbersAsDecimals(prevValue, nextValue),
        currencyCode: prevCurrency || nextCurrency,
    };
};

export const subtractUntaxedAmounts = (prev: UntaxedAmount, next: UntaxedAmount): UntaxedAmount => {
    const { netValue: prevNetValue, grossValue: prevGrossValue, currency: prevCurrency } = prev;
    const { netValue: nextNetValue, grossValue: nextGrossValue, currency: nextCurrency } = next;

    return {
        netValue: subtractNumbersAsDecimals(prevNetValue, nextNetValue),
        grossValue: subtractNumbersAsDecimals(prevGrossValue, nextGrossValue),
        currency: prevCurrency || nextCurrency,
    };
};

export const sumMultiCurrencyAmounts = (prev: MultiCurrencyAmount, next: MultiCurrencyAmount) =>
    mergeWith(
        (prevAmount, nextAmount) => {
            if (prevAmount == null) {
                return nextAmount;
            } else if (nextAmount == null) {
                return prevAmount;
            }

            return sumAmounts(prevAmount, nextAmount);
        },
        prev,
        next
    );

export const reduceAndSumAmounts = reduce(sumAmounts, ZERO_AMOUNT);
export const reduceAndSubtractAmounts = reduce(subtractUntaxedAmounts, ZERO_AMOUNT);

export const createReduceAndSumAmounts = (currencyCode: string) => reduce(sumAmounts, createZeroAmount(currencyCode));

const multiplyTaxValue =
    (count: number) =>
    ({ value, ...other }: TaxValue): TaxValue => ({
        value: multiplyNumbersAsDecimals(value, count),
        ...other,
    });

export const multiplyAmount = ({ grossValue, netValue, taxValues, ...other }: Amount, count: number): Amount => ({
    grossValue: multiplyNumbersAsDecimals(grossValue, count),
    netValue: multiplyNumbersAsDecimals(netValue, count),
    taxValues: map(multiplyTaxValue(count))(taxValues),
    ...other,
});

export const divideAmount = (amount: UntaxedAmount, divisor: number): UntaxedAmount => {
    const { netValue, grossValue, currency } = amount;

    return {
        netValue: divideNumbersAsDecimals(netValue, divisor),
        grossValue: divideNumbersAsDecimals(grossValue, divisor),
        currency,
    };
};

export const divideSimpleAmount = (amount: SimpleAmount, divisor: number): SimpleAmount => {
    const { value, currencyCode } = amount;

    return {
        value: divideNumbersAsDecimals(value, divisor),
        currencyCode,
    };
};

export const multiplyMultiCurrencyAmount = (
    multiCurrencyAmount: MultiCurrencyAmount,
    count: number
): MultiCurrencyAmount => mapValues((amount) => multiplyAmount(amount, count), multiCurrencyAmount);

export const isPositiveAmount = ({ grossValue }: Amount) => {
    const grossDecimal = DecimalService.parse(grossValue);
    const zeroDecimal = DecimalService.parse(0);

    return DecimalService.isGreater(grossDecimal, zeroDecimal);
};

export const isZeroAmount = isMatch(pick(['grossValue', 'netValue'], ZERO_AMOUNT));
export const isZeroSimpleAmount = (amount: SimpleAmount) => amount.value === 0;

export const isMultiCurrencyAmountZeroOrNil = ifElse(isEmpty, T, flow(values, head, isZeroAmount));

export const convertGrossAmountToSimpleAmount = ({ currency, grossValue }: UntaxedAmount): SimpleAmount => ({
    currencyCode: currency,
    value: grossValue,
});

export const convertNetAmountToSimpleAmount = ({ currency, netValue }: UntaxedAmount): SimpleAmount => ({
    currencyCode: currency,
    value: netValue,
});

export const convertToMultiCurrencyAmount = (amount: Amount): MultiCurrencyAmount => ({ [amount.currency]: amount });

export const areGrossAmountsEqual = (firstAmount: Amount, secondAmount: Amount) =>
    firstAmount.currency === secondAmount.currency && firstAmount.grossValue === secondAmount.grossValue;
